import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <>
      <nav className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${scrolled ? 'bg-background-50/95 backdrop-blur-md shadow-sm' : 'bg-transparent'}`}>
        <div className="w-full px-6 md:px-10">
          <div className="flex items-center justify-between h-16 md:h-18">
            <Link to="/" className="flex items-center gap-2">
              <span
                className={`font-heading text-xl md:text-2xl font-semibold transition-all duration-300 ${scrolled ? 'text-foreground-900' : 'text-white drop-shadow-md'}`}
                style={{ textShadow: scrolled ? 'none' : '0 2px 8px rgba(0,0,0,0.4)' }}
              >
                yoybuy<span className={`transition-colors duration-300 ${scrolled ? 'text-primary-500' : 'text-white'}`}>.baby</span>
              </span>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-white/90 hover:text-white'}`}>
                Главная
              </Link>

              <div className="relative" ref={dropdownRef}>
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className={`flex items-center gap-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-white/90 hover:text-white'}`}
                >
                  Категории
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-lg border border-background-200/70 overflow-hidden shadow-lg animate-[fadeIn_0.15s_ease-out]">
                    <div className="py-1 max-h-80 overflow-y-auto">
                      {categories.map((cat) => (
                        <a
                          key={cat.nameEn}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                          onClick={() => setDropdownOpen(false)}
                        >
                          <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                            <i className={cat.icon}></i>
                          </span>
                          <span>{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Link to="/yoybuy-sheet-guide-2026" className={`text-sm font-medium transition-colors whitespace-nowrap ${scrolled ? 'text-foreground-700 hover:text-primary-500' : 'text-white/90 hover:text-white'}`}>
                Руководство
              </Link>
            </div>

            <div className="flex items-center gap-3">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="hidden md:inline-flex items-center gap-1.5 bg-primary-500 text-background-50 px-5 py-2 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all whitespace-nowrap"
              >
                yoybuy sheet
                <i className="ri-arrow-right-line"></i>
              </a>

              <button
                onClick={() => setMobileOpen(!mobileOpen)}
                className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-white/15 backdrop-blur-sm border border-white/20 cursor-pointer hover:bg-white/25 transition-all active:scale-95"
                aria-label={mobileOpen ? 'Закрыть меню' : 'Открыть меню'}
              >
                <div className="w-5 h-5 flex flex-col items-center justify-center gap-[5px] relative">
                  <span className={`block w-5 h-[2px] rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground-900' : 'bg-white'}`} style={{ transform: mobileOpen ? 'translateY(3.5px) rotate(45deg)' : 'none' }}></span>
                  <span className={`block w-5 h-[2px] rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground-900' : 'bg-white'}`} style={{ opacity: mobileOpen ? 0 : 1, transform: mobileOpen ? 'scaleX(0)' : 'none' }}></span>
                  <span className={`block w-5 h-[2px] rounded-full transition-all duration-300 ${scrolled ? 'bg-foreground-900' : 'bg-white'}`} style={{ transform: mobileOpen ? 'translateY(-3.5px) rotate(-45deg)' : 'none' }}></span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-foreground-900/50 backdrop-blur-sm" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 right-0 w-72 h-[calc(100vh-64px)] bg-background-50 overflow-y-auto shadow-xl animate-[slideInRight_0.2s_ease-out]">
            <div className="p-6 space-y-6">
              <div className="space-y-1">
                <Link to="/" className="block py-3 text-base font-medium text-foreground-900 border-b border-background-100" onClick={() => setMobileOpen(false)}>
                  Главная
                </Link>
                <Link to="/yoybuy-sheet-guide-2026" className="block py-3 text-base font-medium text-foreground-900 border-b border-background-100" onClick={() => setMobileOpen(false)}>
                  Руководство
                </Link>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-foreground-500 uppercase tracking-wider mb-3">Категории</h4>
                <div className="space-y-0.5">
                  {categories.map((cat) => (
                    <a
                      key={cat.nameEn}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors"
                      onClick={() => setMobileOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center text-accent-500">
                        <i className={cat.icon}></i>
                      </span>
                      <span>{cat.name}</span>
                    </a>
                  ))}
                </div>
              </div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center bg-primary-500 text-background-50 py-3 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all"
              >
                yoybuy sheet
              </a>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center bg-foreground-900 text-background-50 py-3 rounded-full text-sm font-semibold hover:bg-foreground-800 transition-all"
              >
                buy it now
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
}